import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAffiliate } from '../lib/use-affiliate';
import { useAuth } from '../lib/auth-context';

/**
 * Left sidebar for the customer-portal.
 *
 * Mirrors the apps/admin Sidebar collapsible pattern (OCP-54): two
 * width modes (`w-56` expanded / `w-14` collapsed icon-only), state
 * persists in localStorage so it survives reloads.
 *
 * The nav-item set matches the catalog laid out in
 * `evaluation/adr-orboto-customer-portal.md` (Section "Customer-Portal
 * UI structure"). Per-section content lands in OCP-114..119; this file
 * only owns the navigation shell.
 */
const LOCAL_STORAGE_KEY = 'orboto-portal-sidebar-collapsed';

interface NavItem {
  to: string;
  /** `common` catalogue key under `nav.` (OCP-656). */
  label: string;
  icon: ReactNode;
}

// Inline Heroicons-outline-style SVGs, stroke-width 1.5, viewBox 24×24.
// Inlined to avoid an icon-library dependency at this scale.
const ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.75" y="3.75" width="6.5" height="6.5" rx="1" />
      <rect x="13.75" y="3.75" width="6.5" height="6.5" rx="1" />
      <rect x="3.75" y="13.75" width="6.5" height="6.5" rx="1" />
      <rect x="13.75" y="13.75" width="6.5" height="6.5" rx="1" />
    </svg>
  ),
  products: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3.75 7.5 12 3.75l8.25 3.75v9L12 20.25 3.75 16.5v-9Z" strokeLinejoin="round" />
      <path d="M3.75 7.5 12 11.25l8.25-3.75M12 11.25v9" strokeLinejoin="round" />
    </svg>
  ),
  subscriptions: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v4.5h-4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="m3.75 6.5 8.25 6.5 8.25-6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  licenses: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="5" width="16" height="14" rx="1.5" />
      <path d="M8 9h8M8 12h8M8 15h5" strokeLinecap="round" />
    </svg>
  ),
  billing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.5" y="6" width="17" height="12" rx="1.5" />
      <path d="M3.5 10h17M7 14.5h4" strokeLinecap="round" />
    </svg>
  ),
  apiKeys: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="12" r="3.25" />
      <path d="M11.25 12h9M16.5 12v2.5M19.5 12v2" strokeLinecap="round" />
    </svg>
  ),
  ai: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        d="M12 3.5l1.6 4.4 4.4 1.6-4.4 1.6L12 15.5l-1.6-4.4L6 9.5l4.4-1.6L12 3.5Z"
        strokeLinejoin="round"
      />
      <path d="M18 15.5l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9Z" strokeLinejoin="round" />
    </svg>
  ),
  instances: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.5" y="4.5" width="17" height="6" rx="1.5" />
      <rect x="3.5" y="13.5" width="17" height="6" rx="1.5" />
      <path d="M6.5 7.5h.01M6.5 16.5h.01" strokeLinecap="round" />
    </svg>
  ),
  affiliate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="6.5" cy="7" r="2.5" />
      <circle cx="17.5" cy="7" r="2.5" />
      <circle cx="12" cy="17" r="2.5" />
      <path d="M8.6 8.6 10.9 15M15.4 8.6 13.1 15" strokeLinecap="round" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.08a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.08A1.7 1.7 0 0 0 4.6 8.94a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.08A1.7 1.7 0 0 0 15.06 4.6a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.08A1.7 1.7 0 0 0 19.4 15Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  chevronLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="m14.5 6.5-5.5 5.5 5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevronRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="m9.5 6.5 5.5 5.5-5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// OCP-175: top-level `/api-keys` removed from sidebar to avoid the
// naming conflict with `/mail/api-keys` (Resend-style direct keys).
// Route is still live in App.tsx so direct URLs + OAuth-flow
// redirects keep working — just not nav-prominent. Mail-specific
// OAuth connections stay discoverable via /mail/connections.
const baseItems: NavItem[] = [
  { to: '/', label: 'nav.overview', icon: ICONS.dashboard },
  { to: '/products', label: 'nav.products', icon: ICONS.products },
  { to: '/subscriptions', label: 'nav.subscriptions', icon: ICONS.subscriptions },
  { to: '/instances', label: 'nav.instances', icon: ICONS.instances },
  { to: '/backups', label: 'nav.backups', icon: ICONS.licenses },
  { to: '/mail', label: 'nav.mail', icon: ICONS.mail },
  { to: '/licenses', label: 'nav.licenses', icon: ICONS.licenses },
  { to: '/billing', label: 'nav.billing', icon: ICONS.billing },
  // OCP-496: "orboto AI" usage dashboard - every account has an orboto AI
  // subscription (embeddings first, PAYG over the wallet), so no gating.
  { to: '/ai', label: 'nav.ai', icon: ICONS.ai },
  { to: '/redeem', label: 'nav.redeem', icon: ICONS.apiKeys },
  // OCP-383: refer-a-friend is for EVERY customer (unlike Affiliate, which is
  // an operator-granted capability), so it is a plain base item - no gating.
  { to: '/refer', label: 'nav.refer', icon: ICONS.affiliate },
  { to: '/settings', label: 'nav.settings', icon: ICONS.settings },
];

// OCP-490: the Affiliate item only appears for accounts an operator has
// granted affiliate status (the /affiliate route also bounces a
// non-affiliate to the overview). Inserted before Settings.
const affiliateItem: NavItem = { to: '/affiliate', label: 'nav.affiliate', icon: ICONS.affiliate };

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function NavItemLinks({
  items,
  collapsed,
  onNavigate,
}: {
  items: NavItem[];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === '/'}
          title={collapsed ? t(it.label) : undefined}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm ${
              isActive
                ? 'bg-brand-500 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60'
            } ${collapsed ? 'justify-center' : ''}`
          }
        >
          <span className="block h-5 w-5 shrink-0">{it.icon}</span>
          {!collapsed && <span className="truncate">{t(it.label)}</span>}
        </NavLink>
      ))}
    </>
  );
}

export interface SidebarProps {
  /** OCP-282 — mobile slide-in drawer open state (controlled by AppShell). */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const { t } = useTranslation('common');
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const { status } = useAuth();
  const { affiliate } = useAffiliate(status === 'authenticated');

  // Splice the Affiliate item in before Settings when the account is an
  // affiliate; otherwise the nav is unchanged.
  const items = useMemo<NavItem[]>(() => {
    if (!affiliate) return baseItems;
    const settingsIdx = baseItems.findIndex((it) => it.to === '/settings');
    const at = settingsIdx === -1 ? baseItems.length : settingsIdx;
    return [...baseItems.slice(0, at), affiliateItem, ...baseItems.slice(at)];
  }, [affiliate]);

  useEffect(() => {
    setCollapsed(readCollapsed());
  }, []);

  // OCP-282 — close the mobile drawer on Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <>
      {/* Desktop: static collapsible aside (md+). */}
      <aside
        className={`hidden shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 md:block ${
          collapsed ? 'w-14' : 'w-56'
        }`}
      >
        <div className={`flex ${collapsed ? 'justify-center' : 'justify-end'} px-2 pt-3`}>
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
            className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-200/70 hover:text-gray-800 dark:hover:text-gray-100"
          >
            <span className="block h-4 w-4">
              {collapsed ? ICONS.chevronRight : ICONS.chevronLeft}
            </span>
          </button>
        </div>
        <nav className="mt-2 flex flex-col gap-1 px-2">
          <NavItemLinks items={items} collapsed={collapsed} />
        </nav>
      </aside>

      {/* Mobile: slide-in drawer overlay (<md), controlled by AppShell. */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40"
            aria-hidden="true"
            onClick={onMobileClose}
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 max-w-[80%] flex-col border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-xl">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4">
              <span className="text-sm font-medium tracking-tight text-gray-500">{t('nav.menu')}</span>
              <button
                type="button"
                onClick={onMobileClose}
                aria-label={t('nav.closeMenu')}
                className="flex h-8 w-8 items-center justify-center rounded text-gray-500 hover:bg-gray-200/70 hover:text-gray-800 dark:hover:text-gray-100"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <nav className="mt-2 flex flex-col gap-1 overflow-y-auto px-2 pb-4">
              <NavItemLinks items={items} collapsed={false} onNavigate={onMobileClose} />
            </nav>
          </aside>
        </div>
      )}
    </>
  );
}
